// Real-time order tracker: shows every order the signed-in customer has
// placed (credit packs + premium contracts) with a live status timeline
// (Created → Processing → Paid → Invoice sent). Uses Supabase Realtime to
// react instantly when the PayFast ITN webhook updates the row.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { listMyPayments } from "@/lib/api/portal.functions";
import { listMyInvoices } from "@/lib/api/invoice-payment.functions";
import { launchPayfastCheckout } from "@/lib/payfast-launch";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, CheckCircle2, Clock, CreditCard, FileCheck2, Mail, RefreshCcw, Radio, Wallet } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/orders")({
  component: OrdersTrackerPage,
});

type Step = { key: string; label: string; icon: any; at?: string | null };

function fmt(d?: string | null) {
  if (!d) return "";
  try { return new Date(d).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

function statusLabel(s?: string | null) {
  const n = (s ?? "pending").toLowerCase();
  if (n === "paid") return { text: "Paid", cls: "bg-emerald-600" };
  if (n === "failed") return { text: "Failed", cls: "bg-rose-600" };
  if (n === "cancelled") return { text: "Cancelled", cls: "bg-slate-500" };
  if (n === "processing") return { text: "Processing", cls: "bg-blue-600" };
  return { text: "Awaiting payment", cls: "bg-amber-500" };
}

function OrdersTrackerPage() {
  const fetchPayments = useServerFn(listMyPayments);
  const fetchInvoices = useServerFn(listMyInvoices);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["portal-orders-tracker"],
    queryFn: () => fetchPayments({}),
    refetchInterval: 30_000,
  });
  const invoicesQ = useQuery({
    queryKey: ["portal-orders-invoices"],
    queryFn: () => fetchInvoices(),
    refetchInterval: 60_000,
  });
  const [payingRef, setPayingRef] = useState<string | null>(null);
  const payments: any[] = data?.payments ?? [];
  const invoices: any[] = invoicesQ.data?.invoices ?? [];
  const outstandingCents = invoicesQ.data?.outstanding_cents ?? 0;
  const paidCents = invoicesQ.data?.paid_cents ?? 0;
  const outstandingInvoices = invoices.filter((i) => i.status !== "paid" && i.status !== "cancelled");
  const zar = (c: number) => `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  async function payInvoice(ref: string) {
    setPayingRef(ref);
    try { await launchPayfastCheckout(ref); }
    finally { setTimeout(() => setPayingRef(null), 4000); }
  }


  // Live updates: any change to the user's credit_orders or contract_orders
  // refetches the list so the timeline reflects the PayFast webhook outcome
  // immediately instead of waiting for the next poll.
  useEffect(() => {
    const channel = supabase
      .channel("portal-orders-tracker")
      .on("postgres_changes", { event: "*", schema: "public", table: "credit_orders" }, () => refetch())
      .on("postgres_changes", { event: "*", schema: "public", table: "contract_orders" }, () => refetch())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Activity className="h-6 w-6" /> Order tracker
          </h1>
          <p className="text-sm text-muted-foreground">
            Live status for every order you've placed. Updates appear here the
            moment PayFast confirms your payment — no need to refresh.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 border-emerald-500 text-emerald-700">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> Live
          </Badge>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </header>

      {/* Account balances */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="py-4">
            <p className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Wallet className="h-3 w-3" /> Outstanding balance
            </p>
            <p className="mt-1 text-2xl font-semibold text-amber-700">{zar(outstandingCents)}</p>
            <p className="text-[11px] text-muted-foreground">{outstandingInvoices.length} invoice(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Paid to date</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-700">{zar(paidCents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex h-full items-center justify-between gap-2 py-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Full invoice history</p>
              <p className="text-xs text-muted-foreground">Statements, receipts &amp; PDF downloads</p>
            </div>
            <Button asChild size="sm" variant="outline">
              <Link to="/portal/invoices">Open</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {outstandingInvoices.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-amber-600" /> Outstanding invoices — pay online instantly
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {outstandingInvoices.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2">
                <div>
                  <div className="font-mono text-xs">{inv.reference}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Due {inv.due_date ?? "—"} · {zar(inv.total_cents)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => payInvoice(inv.reference)} disabled={payingRef === inv.reference}>
                    {payingRef === inv.reference ? "Opening PayFast…" : "Pay now"}
                  </Button>
                  <a
                    href={`/api/public/invoice-pdf?ref=${encodeURIComponent(inv.reference)}&t=${inv.public_token}`}
                    className="inline-flex items-center rounded-md border border-border px-2 text-xs hover:bg-secondary"
                  >
                    PDF
                  </a>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}


      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading your orders…</p>
      ) : payments.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don't have any orders yet. Premium purchases will appear here
            with a live status timeline.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {payments.map((p: any) => {
            const s = (p.status ?? "pending").toLowerCase();
            const isPaid = s === "paid";
            const isFailed = s === "failed" || s === "cancelled";
            const steps: Step[] = [
              { key: "created", label: "Order created", icon: CreditCard, at: p.created_at },
              {
                key: "processing",
                label: isFailed ? "Payment failed" : "Payment processing",
                icon: Clock,
                at: isPaid || isFailed ? p.paid_at ?? p.created_at : null,
              },
              { key: "paid", label: "Paid via PayFast", icon: CheckCircle2, at: isPaid ? p.paid_at : null },
              {
                key: "invoice",
                label: "Confirmed invoice sent",
                icon: Mail,
                at: p.receipt_sent_at ?? null,
              },
            ];
            const badge = statusLabel(p.status);
            return (
              <Card key={`${p.kind}:${p.reference}`} className="overflow-hidden">
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-base">{p.title}</CardTitle>
                    <div className="font-mono text-[11px] text-muted-foreground">{p.reference}</div>
                  </div>
                  <div className="text-right">
                    <Badge className={`${badge.cls} text-white`}>{badge.text}</Badge>
                    <div className="mt-1 text-sm font-semibold">R{Number(p.amount_zar).toFixed(2)}</div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ol className="grid gap-2 md:grid-cols-4">
                    {steps.map((step, idx) => {
                      const reached = !!step.at || (idx === 0);
                      const Icon = step.icon;
                      const failedHere = isFailed && step.key === "processing";
                      return (
                        <li
                          key={step.key}
                          className={`rounded-lg border p-3 text-xs ${
                            failedHere
                              ? "border-rose-300 bg-rose-50"
                              : reached
                                ? "border-emerald-300 bg-emerald-50"
                                : "border-dashed border-border bg-muted/30 text-muted-foreground"
                          }`}
                        >
                          <div className="mb-1 flex items-center gap-1.5 font-medium">
                            <Icon className={`h-3.5 w-3.5 ${failedHere ? "text-rose-700" : reached ? "text-emerald-700" : ""}`} />
                            {step.label}
                          </div>
                          <div className="text-[11px]">{fmt(step.at) || (reached ? "—" : "Pending")}</div>
                        </li>
                      );
                    })}
                  </ol>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {!isPaid && !isFailed && (
                      <Button size="sm" onClick={() => payInvoice(p.reference)} disabled={payingRef === p.reference}>
                        {payingRef === p.reference ? "Opening PayFast…" : "Continue payment"}
                      </Button>
                    )}
                    {isFailed && (
                      <Button size="sm" onClick={() => payInvoice(p.reference)} disabled={payingRef === p.reference}>
                        {payingRef === p.reference ? "Opening PayFast…" : "Retry payment"}
                      </Button>
                    )}
                    {isPaid && !p.receipt_sent_at && (
                      <Badge variant="outline" className="gap-1">
                        <FileCheck2 className="h-3.5 w-3.5" /> Awaiting admin invoice
                      </Badge>
                    )}
                    {isPaid && p.receipt_sent_at && (
                      <Badge className="gap-1 bg-emerald-600 text-white">
                        <Mail className="h-3.5 w-3.5" /> Invoice emailed {fmt(p.receipt_sent_at)}
                      </Badge>
                    )}
                    <Button asChild size="sm" variant="outline">
                      <Link to="/portal/my-payments">View PDFs</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
